import * as React from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles, tokens } from "@fluentui/react-components";
import { parseMentions } from "./richtext/parser";
import { ExcelService } from "../../../services/excel.service";
import { useWorkflowIntelligence } from "../../../contexts/WorkflowIntelligenceContext";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userMention: {
    display: "inline",
    padding: `1px ${tokens.spacingHorizontalXXS}`,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: "inherit",
    lineHeight: "inherit",
    verticalAlign: "baseline",
  },
  cellMention: {
    display: "inline",
    padding: `1px ${tokens.spacingHorizontalXXS}`,
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    borderRadius: tokens.borderRadiusSmall,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: "inherit",
    lineHeight: "inherit",
    verticalAlign: "baseline",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorPaletteGreenBackground1,
      textDecoration: "underline",
    },
  },
  sheetMention: {
    display: "inline",
    padding: `1px ${tokens.spacingHorizontalXXS}`,
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    borderRadius: tokens.borderRadiusSmall,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: "inherit",
    lineHeight: "inherit",
    verticalAlign: "baseline",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorPaletteGreenBackground1,
      textDecoration: "underline",
    },
  },
  statusItemMention: {
    display: "inline",
    padding: `1px ${tokens.spacingHorizontalXXS}`,
    backgroundColor: tokens.colorPaletteDarkOrangeBackground2,
    color: tokens.colorPaletteDarkOrangeForeground2,
    border: `1px solid ${tokens.colorPaletteDarkOrangeBorder2}`,
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: "inherit",
    lineHeight: "inherit",
    verticalAlign: "baseline",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorPaletteDarkOrangeBackground1,
      textDecoration: "underline",
    },
  },
  missingSheetMention: {
    display: "inline",
    padding: `1px ${tokens.spacingHorizontalXXS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: "inherit",
    lineHeight: "inherit",
    verticalAlign: "baseline",
    fontStyle: "italic",
  },
});

interface RichTextDisplayProps {
  /**
   * Markup string to display
   * Example: "Check out @[John Doe](john@example.com) in cell @[A1](A1)"
   */
  markup: string;
}

/**
 * Display component for rich text content with interactive mentions
 *
 * Features:
 * - Parses markup format: "@[Display](id)" for both user mentions and cell references
 * - User mentions: Displayed with blue background (@John Doe)
 * - Cell references: Displayed with green background, clickable to select cell in Excel
 * - Keyboard accessible: Cell references support Enter/Space key activation
 *
 * @example
 * <RichTextDisplay markup="Check out @[John Doe](john@example.com) in @[Sheet1!A1](Sheet1!A1)" />
 */
const RichTextDisplay: React.FC<RichTextDisplayProps> = ({ markup }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const navigate = useNavigate();
  const { getWorksheetName, isStandalone } = useWorkflowIntelligence();

  if (!markup) {
    return null;
  }

  // Parse mentions from markup
  const mentions = parseMentions(markup);

  // If no mentions, just display plain text
  if (mentions.length === 0) {
    return <span className={`${typography.bodyLarge} ${styles.container}`}>{markup}</span>;
  }

  // Build segments array with text and mentions
  const segments: Array<{ type: "text" | "mention"; content: string; mentionType?: "user" | "cell" | "reference"; display?: string }> = [];
  let lastIndex = 0;

  // Sort mentions by position
  const sortedMentions = [...mentions].sort((a, b) => a.startIndex - b.startIndex);

  for (const mention of sortedMentions) {
    // Add text before this mention
    if (mention.startIndex > lastIndex) {
      segments.push({
        type: "text",
        content: markup.substring(lastIndex, mention.startIndex),
      });
    }

    // Add the mention
    segments.push({
      type: "mention",
      content: mention.id,
      mentionType: mention.type,
      display: mention.display,
    });

    lastIndex = mention.endIndex;
  }

  // Add remaining text after last mention
  if (lastIndex < markup.length) {
    segments.push({
      type: "text",
      content: markup.substring(lastIndex),
    });
  }

  return (
    <span className={`${typography.bodyLarge} ${styles.container}`}>
      {segments.map((segment, index) => {
        if (segment.type === "text") {
          return <React.Fragment key={index}>{segment.content}</React.Fragment>;
        }

        if (segment.type === "mention" && segment.display) {
          if (segment.mentionType === "user") {
            return (
              <span key={index} className={styles.userMention}>
                @{segment.display}
              </span>
            );
          } else if (segment.mentionType === "cell") {
            const handleCellClick = async () => {
              // No Excel → nothing to jump to. Leave the visual mention
              // in place but skip the navigation attempt.
              if (isStandalone) return;
              try {
                await ExcelService.selectCell(segment.content);
              } catch (error) {
                console.error("Failed to select cell:", error);
              }
            };
            return (
              <span
                key={index}
                className={styles.cellMention}
                onClick={handleCellClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCellClick();
                  }
                }}
              >
                {segment.display}
              </span>
            );
          } else if (segment.mentionType === "reference") {
            // Parse the reference ID (format: "sheet:{GUID}" or "statusItem:123")
            const [refType, ...idParts] = segment.content.split(':');
            const refId = idParts.join(':');
            const isSheet = refType === 'sheet';

            if (isSheet) {
              // Resolve sheet ID to name using context cache
              const sheetName = getWorksheetName(refId);

              if (!sheetName) {
                // Two very different reasons a sheet name can't resolve:
                //   • Standalone: no Excel connection, so EVERY sheet
                //     reference in every comment falls into this branch.
                //     "[Missing sheet]" on every chip reads like a fleet
                //     of errors — show a neutral "sheet" chip instead
                //     that preserves the sentence flow.
                //   • Excel: sheet actually deleted or renamed — keep
                //     the loud "[Missing sheet]" indicator so the author
                //     knows their reference is broken.
                return (
                  <span key={index} className={styles.missingSheetMention}>
                    {isStandalone ? "sheet" : "[Missing sheet]"}
                  </span>
                );
              }

              const handleSheetClick = async () => {
                // No Excel → no sheet to jump to. Same reasoning as the
                // cell mention above.
                if (isStandalone) return;
                try {
                  await ExcelService.selectCellById(refId, "A1");
                } catch (error) {
                  console.error("Failed to navigate to sheet:", error);
                }
              };

              return (
                <span
                  key={index}
                  className={styles.sheetMention}
                  onClick={handleSheetClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSheetClick();
                    }
                  }}
                >
                  {sheetName}
                </span>
              );
            }

            // Status item reference
            const handleStatusItemClick = () => {
              navigate(`/workflow-status?search=${encodeURIComponent(segment.display || '')}`);
            };

            return (
              <span
                key={index}
                className={styles.statusItemMention}
                onClick={handleStatusItemClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleStatusItemClick();
                  }
                }}
              >
                #{segment.display}
              </span>
            );
          }
        }

        return null;
      })}
    </span>
  );
};

export default RichTextDisplay;
